import React, { Suspense, lazy, useState, useEffect, useCallback } from 'react';
import { Routes, Route, useLocation, useNavigate, BrowserRouter } from 'react-router-dom';
import FadeTransition from './components/Animations/FadeTransition';
import { BottomSheetProvider } from './components/Modals/BottomSheet/BottomSheetContext';
import { SelectedServicesProvider } from './components/Sections/ServicesSection/SelectedServicesContext';

import Navigation from './components/Nav/Navigation/Navigation';
import Footer from './components/Nav/Footer/Footer';
import CookieBanner from './components/CookieBanner/CookieBanner';
import 'flag-icons/css/flag-icons.min.css';

import HomePage from './pages/Home/Home';
import About from './pages/About/About';

const Portfolio = lazy(() => import('./pages/Portfolio/portfolio'));
const ProjectPage = lazy(() => import('./pages/ProjectPage/ProjectPage'));
const Legal = lazy(() => import('./pages/Legal/PrivacyStatement'));
const Cookies = lazy(() => import('./pages/Legal/Cookies'));

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h4>Daar gaat iets fout...</h4>;
    }

    return this.props.children;
  }
}

const StyleWrapper = ({ children, isDarkPage, shouldInvertNav }) => {
  useEffect(() => {
    const bodyElement = document.body;
    const linkButtons = document.querySelectorAll('button.link');
    const headings = document.querySelectorAll('h1, h2, h4, h5, h6');
    const footerlinks = document.querySelectorAll('.footer-p');
    const seperator = document.querySelectorAll('.seperator');
    const hasBodyLCookieClass = [...document.querySelectorAll('p')].some(paragraph => paragraph.classList.contains('body-l cookie'));

    if (isDarkPage && !hasBodyLCookieClass) {
      bodyElement.style.color = 'var(--color-neutral-0)';
      linkButtons.forEach(button => {
        button.style.color = 'var(--color-neutral-0)';
      });
      headings.forEach(heading => {
        heading.style.color = 'var(--color-neutral-0)';
      });
      seperator.forEach(seperator => {
        seperator.style.backgroundColor = 'var(--color-neutral-0)';
      });
      footerlinks.forEach(footerlinks => {
        footerlinks.style.color = 'var(--color-neutral-0)';
      });
    } else {
      bodyElement.style.background = '';
      bodyElement.style.color = '';
      linkButtons.forEach(button => {
        button.style.color = '';
      });
      headings.forEach(heading => {
        heading.style.color = '';
      });
      footerlinks.forEach(footerlinks => {
        footerlinks.style.color = '';
      });
    }
  }, [isDarkPage]);

  return children;
}

const ScrollTo = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [prevLocation, setPrevLocation] = useState(location);
  const [isDarkPage, setIsDarkPage] = useState(false);
  const [shouldInvertNav, setShouldInvertNav] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      setShouldInvertNav(scrollPosition > windowHeight);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToForm = useCallback(() => {
    const formElement = document.getElementById('Form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    const handleScrollToForm = () => {
      setTimeout(scrollToForm, 100);
    };

    window.addEventListener('scrollToForm', handleScrollToForm);

    return () => {
      window.removeEventListener('scrollToForm', handleScrollToForm);
    };
  }, [scrollToForm]);

  useEffect(() => {
    if (location.state?.scrollToForm) {
      window.dispatchEvent(new Event('scrollToForm'));
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  return (
    <div className="app-container">
      <StyleWrapper isDarkPage={isDarkPage} shouldInvertNav={shouldInvertNav}>
        <ScrollTo />
        <CookieBanner />
        <Navigation isFixed={location.pathname === '/'} isDarkPage={isDarkPage} shouldInvertNav={shouldInvertNav} />
        <FadeTransition shouldAnimate={location.pathname !== prevLocation.pathname}>
          <div id="main-wrapper">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<About />} />
              <Route
                path="/portfolio"
                element={
                  <Suspense fallback={<div>Aan het laden...</div>}>
                    <Portfolio />
                  </Suspense>
                }
              />
              <Route
                path="/portfolio/:projectId"
                element={
                  <Suspense fallback={<div>Aan het laden...</div>}>
                    <ProjectPage />
                  </Suspense>
                }
              />
              <Route
                path="/legal/legal"
                element={
                  <Suspense fallback={<div>Aan het laden...</div>}>
                    <Legal />
                  </Suspense>
                }
              />
              <Route
                path="/legal/cookies"
                element={
                  <Suspense fallback={<div>Aan het laden...</div>}>
                    <Cookies />
                  </Suspense>
                }
              />
            </Routes>
          </div>
          <Footer />
        </FadeTransition>
      </StyleWrapper>
    </div>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <BottomSheetProvider>
        <SelectedServicesProvider>
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
        </SelectedServicesProvider>
      </BottomSheetProvider>
    </BrowserRouter>
  );
};

export default App;